import React, { useCallback } from "react";
import Snackbar from "@mui/material/Snackbar";

/**
 * Shared toast notification — a dismissible, auto-hiding acknowledgement for
 * one-off outcomes (an action succeeded, a save failed, etc.), replacing the
 * inline banners that used to get built ad hoc per screen.
 *
 * Callers own the `open` state and are responsible for resetting it back to
 * false in `onClose` (e.g. `onClose={() => setStatus(null)}`); that function
 * must be referentially stable (wrap it in `useCallback`) or every parent
 * re-render will re-arm MUI's autoHideDuration timer and the snackbar will
 * never auto-dismiss — this component only guards its own internal handler,
 * it can't fix an unstable `onClose` passed in from outside.
 *
 * Matches V3's actual toast exactly (confirmed against a screenshot of it
 * live, not just the static reference export, which never renders one): a
 * solid dark-navy pill with a small colored dot on the left, white text, no
 * visible close button — not MUI's stock bold-filled Alert look this
 * previously used. Built as a plain styled div rather than MUI's Alert
 * component since Alert's icon/action slots fight this simpler shape more
 * than they help.
 */
const DOT_COLOR = {
  success: "var(--paid-primary)",
  error: "var(--paid-danger)",
  warning: "var(--paid-warning)",
  info: "var(--paid-primary)",
};

const AppSnackbar = ({
  open,
  onClose,
  message,
  severity = "info",
  autoHideDuration = 5000,
  anchorOrigin = { vertical: "bottom", horizontal: "left" },
}) => {
  const handleClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") return;
      onClose?.();
    },
    [onClose]
  );

  const dotColor = DOT_COLOR[severity] || DOT_COLOR.info;

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
    >
      <div
        role="status"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          background: "var(--paid-brand-dark)",
          color: "var(--paid-white)",
          borderRadius: "999px",
          padding: "14px 22px",
          fontFamily: "var(--paid-font)",
          fontSize: "15px",
          fontWeight: 500,
          lineHeight: 1.4,
          boxShadow: "0 8px 24px rgba(14, 48, 62, 0.24)",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: "none",
            width: "8px",
            height: "8px",
            borderRadius: "999px",
            background: dotColor,
          }}
        />
        <span>{message}</span>
      </div>
    </Snackbar>
  );
};

export default AppSnackbar;
